import React, { useState, useRef, useEffect } from 'react';
import { Input } from 'antd';
import styled, { keyframes } from 'styled-components';
import { generateAiContent } from '../../../Apis/Jobs';

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`;

const bounce = keyframes`
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-4px); }
`;

const Fab = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #47a4ea;
  border: none;
  cursor: pointer;
  display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #fff;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  z-index: 999;
  transition: transform 0.2s;
  &:hover {
    transform: scale(1.08);
  }
`;

const Panel = styled.div`
  position: fixed;
  bottom: 80px;
  right: 24px;
  width: 360px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.09);
  border-radius: 14px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.15);
  z-index: 999;
  flex-direction: column;
  overflow: hidden;
  max-height: 520px;
  display: ${({ $open }) => ($open ? 'flex' : 'none')};
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: #47a4ea;
  color: #fff;
  flex-shrink: 0;
`;

const HeaderTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 7px;
`;

const PulseDot = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #fff;
  opacity: 0.7;
  animation: ${pulse} 1.8s ease-in-out infinite;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  font-size: 16px;
  opacity: 0.8;
  padding: 0;
  line-height: 1;
  &:hover {
    opacity: 1;
  }
`;

const Messages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 160px;
`;

const Msg = styled.div`
  max-width: 88%;
  padding: 8px 11px;
  border-radius: 10px;
  font-size: 12px;
  line-height: 1.55;
  ${({ $role }) => {
    if ($role === 'user')
      return `
        background: #47a4ea;
        color: #fff;
        border-bottom-right-radius: 3px;
        align-self: flex-end;
      `;
    if ($role === 'ai')
      return `
        background: #f0f8ff;
        color: #1a1917;
        border-bottom-left-radius: 3px;
        align-self: flex-start;
        border: 1px solid #b3cef0;
      `;
    return `
      background: #f9f8f6;
      color: #a09e99;
      font-size: 11px;
      align-self: center;
      border: 1px solid rgba(0,0,0,0.09);
      border-radius: 8px;
      font-style: italic;
      max-width: 94%;
    `;
  }}
`;

const ConfirmCard = styled.div`
  background: #f0f8ff;
  border: 1px solid #b3cef0;
  border-radius: 10px;
  border-bottom-left-radius: 3px;
  padding: 10px 11px;
  font-size: 12px;
  align-self: flex-start;
  max-width: 94%;
`;

const ConfirmText = styled.div`
  color: #1a1917;
  margin-bottom: 8px;
  line-height: 1.5;
`;

const ConfirmBtns = styled.div`
  display: flex;
  gap: 6px;
`;

const ApplyBtn = styled.button`
  background: #47a4ea;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  &:hover {
    background: #3593d9;
  }
`;

const DiscardBtn = styled.button`
  background: #f9f8f6;
  color: #595959;
  border: 1px solid rgba(0, 0, 0, 0.09);
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 11px;
  cursor: pointer;
  font-family: inherit;
  &:hover {
    background: #f0f0f0;
  }
`;

const TypingDots = styled.div`
  align-self: flex-start;
  background: #f0f8ff;
  border: 1px solid #b3cef0;
  border-radius: 10px;
  border-bottom-left-radius: 3px;
  padding: 9px 13px;
  display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
  align-items: center;
  gap: 4px;
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #47a4ea;
  display: inline-block;
  animation: ${bounce} 0.9s ease-in-out infinite;
  &:nth-child(2) {
    animation-delay: 0.15s;
  }
  &:nth-child(3) {
    animation-delay: 0.3s;
  }
`;

const InputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  padding: 10px 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.09);
  flex-shrink: 0;
  background: #f9f8f6;
`;

const SendBtn = styled.button`
  background: #47a4ea;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
  font-size: 14px;
  transition: background 0.15s;
  &:hover {
    background: #3593d9;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const INITIAL_MESSAGE = {
  id: 0,
  role: 'system',
  content:
    'Tell me what to change — I can add, edit, or remove headings, update settings, write AI prompts, and more.'
};

const extractDefaultValueText = html => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  tmp.querySelectorAll('[data-action="k2p-remove"]').forEach(el => el.remove());
  return tmp.textContent.replace(/\s+/g, ' ').trim();
};

const buildSystemPrompt = (draft, k2ps) => {
  const snapshot = {
    templateName: draft?.templateName || '',
    templateDescription: draft?.templateDescription || '',
    sectionHeadings: (draft?.sectionHeadings || []).map(h => ({
      customUIHeading: h.customUIHeading,
      systemHeading: h.systemHeading,
      description: h.description,
      defaultValue: extractDefaultValueText(h.defaultValue),
      appendToDefault: h.appendToDefault,
      submitToEmr: h.submitToEmr,
      suppressOnEmpty: h.suppressOnEmpty,
      useAI: h.useAI,
      aiPrompt: h.aiPrompt,
      required: h.required
    }))
  };

  const eligibleK2ps = (k2ps || []).filter(k => !k.restrictedToTemplates);
  const k2pText = eligibleK2ps.length
    ? eligibleK2ps
        .map(
          k =>
            `key: ${k.key} | phrase: ${(k.phrase || '').replace(/<[^>]*>/g, '').trim()}`
        )
        .join('\n')
    : 'none';

  return `You are an AI assistant helping configure a CascadeMD clinical note template. The user will describe changes to make. Respond with ONLY a JSON object — no markdown, no prose, no explanation outside the JSON.

Current template state:
${JSON.stringify(snapshot, null, 2)}

Available K2Ps (use exact key name when setting defaultValue to a K2P):
${k2pText}

Response format:
{
  "type": "minor" | "structural",
  "summary": "one sentence describing what will change",
  "changes": [<change objects>]
}

"minor" — field updates: toggles, descriptions, AI prompts, default values. Auto-applied without confirmation.
"structural" — structural changes: add, remove, or rename headings. Requires user confirmation before applying.

Change object formats:

Set a field on an existing heading:
{"op":"setField","headingName":"exact customUIHeading value","field":"required|appendToDefault|submitToEmr|useAI|suppressOnEmpty|description|aiPrompt|defaultValue","value":<boolean or string>}

Add a new heading:
{"op":"addHeading","afterHeading":"exact heading name to insert after, or null to append at end","heading":{"customUIHeading":"","systemHeading":"None","description":"","defaultValue":"","appendToDefault":false,"submitToEmr":true,"suppressOnEmpty":false,"useAI":false,"aiPrompt":"","required":false}}

Remove a heading:
{"op":"removeHeading","headingName":"exact customUIHeading value"}

Rename a heading:
{"op":"renameHeading","headingName":"exact customUIHeading value","newName":"new name"}

Update template-level fields:
{"op":"setTemplateName","value":"new name"}
{"op":"setTemplateDescription","value":"new description"}

Rules:
- systemHeading must always be "None"
- Match headingName exactly to the customUIHeading in the current template state
- Only include changes that will actually modify the current state. Do not include a setField operation if the field already has the requested value.
- useAI constraint: only ONE section per template may have useAI set to true at a time. If the user asks to enable useAI on a section, first check the current template state. If any other section currently has useAI: true, include a setField op to set useAI: false on that section before setting it to true on the target section.
- When enabling useAI on a section, always also include a setField op to set a meaningful aiPrompt for that section based on its heading name, unless the user has explicitly said not to. A good aiPrompt describes what the AI should generate — e.g. "Summarize the patient's assessment and plan from the dictation."
- defaultValue — two cases:
  1. K2P reference: if the user wants to insert a K2P into a section's default value, you MUST use the exact key string from the Available K2Ps list above. Do not guess, approximate, or substitute a different K2P. If the user's description does not match any key in the list exactly or closely enough to be certain, return a type:error instead. Do not wrap the key in \${} or any other syntax.
  2. Plain text: if the user provides a custom text string (not referencing a K2P), use it directly.
- If the request is unclear or cannot be fulfilled: {"type":"error","summary":"brief explanation of the problem"}
- Return ONLY valid JSON. Never wrap in markdown code blocks.`;
};

const AIChatPanel = ({
  draft,
  setDraft,
  k2ps = [],
  visible,
  templateId,
  applyPillsFromText,
  headingContentRefs
}) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const msgCounter = useRef(1);
  const msgsEndRef = useRef(null);

  useEffect(() => {
    setMessages([INITIAL_MESSAGE]);
    setOpen(false);
    setInput('');
  }, [templateId]);

  useEffect(() => {
    if (msgsEndRef.current) msgsEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, pending]);

  const addMessage = msg => {
    const id = ++msgCounter.current;
    setMessages(prev => [...prev, { id, ...msg }]);
    return id;
  };

  const applyChanges = changes => {
    const domUpdates = [];

    setDraft(prev => {
      let updated = {
        ...prev,
        sectionHeadings: [...(prev.sectionHeadings || [])]
      };

      changes.forEach(c => {
        if (c.op === 'setField') {
          const idx = updated.sectionHeadings.findIndex(
            h => h.customUIHeading === c.headingName
          );
          if (idx !== -1) {
            let value = c.value;
            if (c.field === 'defaultValue' && applyPillsFromText) {
              value = applyPillsFromText(c.value, k2ps) || c.value;
              domUpdates.push({ idx, html: value });
            }
            updated.sectionHeadings = updated.sectionHeadings.map((h, i) =>
              i === idx ? { ...h, [c.field]: value } : h
            );
          }
        } else if (c.op === 'addHeading') {
          const newH = {
            customUIHeading: c.heading?.customUIHeading || 'New Section',
            systemHeading: 'None',
            description: c.heading?.description || '',
            defaultValue: c.heading?.defaultValue || '',
            appendToDefault: !!c.heading?.appendToDefault,
            submitToEmr: c.heading?.submitToEmr !== false,
            suppressOnEmpty: !!c.heading?.suppressOnEmpty,
            useAI: !!c.heading?.useAI,
            aiPrompt: c.heading?.aiPrompt || '',
            required: !!c.heading?.required
          };
          if (c.afterHeading) {
            const idx = updated.sectionHeadings.findIndex(
              h => h.customUIHeading === c.afterHeading
            );
            const insertAt =
              idx !== -1 ? idx + 1 : updated.sectionHeadings.length;
            const arr = [...updated.sectionHeadings];
            arr.splice(insertAt, 0, newH);
            updated.sectionHeadings = arr;
          } else {
            updated.sectionHeadings = [...updated.sectionHeadings, newH];
          }
        } else if (c.op === 'removeHeading') {
          updated.sectionHeadings = updated.sectionHeadings.filter(
            h => h.customUIHeading !== c.headingName
          );
        } else if (c.op === 'renameHeading') {
          updated.sectionHeadings = updated.sectionHeadings.map(h =>
            h.customUIHeading === c.headingName
              ? { ...h, customUIHeading: c.newName }
              : h
          );
        } else if (c.op === 'setTemplateName') {
          updated.templateName = c.value;
        } else if (c.op === 'setTemplateDescription') {
          updated.templateDescription = c.value;
        }
      });

      return updated;
    });

    if (domUpdates.length && headingContentRefs) {
      setTimeout(() => {
        domUpdates.forEach(({ idx, html }) => {
          const el = headingContentRefs.current?.[idx];
          if (el) el.innerHTML = html;
        });
      }, 0);
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || pending) return;
    setInput('');
    addMessage({ role: 'user', content: text });
    setPending(true);

    try {
      const payload = [
        { role: 'system', content: buildSystemPrompt(draft, k2ps) },
        { role: 'user', content: text }
      ];
      const response = await generateAiContent(payload);
      const rawJson = response?.data?.result?.[0];
      if (!rawJson) throw new Error('No response received. Please try again.');

      const action = JSON.parse(rawJson.replace(/```json|```/g, '').trim());

      if (action.type === 'error') {
        addMessage({ role: 'ai', content: action.summary });
      } else if (action.type === 'minor') {
        applyChanges(action.changes);
        addMessage({ role: 'ai', content: action.summary });
      } else if (action.type === 'structural') {
        addMessage({
          role: 'confirm',
          content: action.summary,
          changes: action.changes
        });
      }
    } catch (err) {
      addMessage({
        role: 'system',
        content: err.message || 'Something went wrong. Please try again.'
      });
    } finally {
      setPending(false);
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleApply = (msgId, changes) => {
    applyChanges(changes);
    setMessages(prev =>
      prev.map(m => (m.id === msgId ? { ...m, resolved: 'applied' } : m))
    );
  };

  const handleDiscard = msgId => {
    setMessages(prev =>
      prev.map(m => (m.id === msgId ? { ...m, resolved: 'discarded' } : m))
    );
  };

  return (
    <>
      <Fab
        $visible={visible}
        onClick={() => setOpen(o => !o)}
        title='AI Template Assistant'
      >
        ✦
      </Fab>

      <Panel $open={open}>
        <PanelHeader>
          <HeaderTitle>
            <PulseDot />
            Template Assistant
          </HeaderTitle>
          <CloseBtn onClick={() => setOpen(false)}>✕</CloseBtn>
        </PanelHeader>

        <Messages>
          {messages.map(msg => {
            if (msg.role === 'confirm') {
              if (msg.resolved) {
                return (
                  <ConfirmCard key={msg.id}>
                    <ConfirmText style={{ color: '#8c8c8c', fontStyle: 'italic' }}>
                      {msg.resolved === 'applied' ? '✓ Applied' : '✕ Discarded'}
                    </ConfirmText>
                  </ConfirmCard>
                );
              }
              return (
                <ConfirmCard key={msg.id}>
                  <ConfirmText>{msg.content}</ConfirmText>
                  <ConfirmBtns>
                    <ApplyBtn onClick={() => handleApply(msg.id, msg.changes)}>
                      Apply
                    </ApplyBtn>
                    <DiscardBtn onClick={() => handleDiscard(msg.id)}>
                      Discard
                    </DiscardBtn>
                  </ConfirmBtns>
                </ConfirmCard>
              );
            }
            return (
              <Msg key={msg.id} $role={msg.role}>
                {msg.content}
              </Msg>
            );
          })}
          <TypingDots $visible={pending}>
            <Dot />
            <Dot />
            <Dot />
          </TypingDots>
          <div ref={msgsEndRef} />
        </Messages>

        <InputRow>
          <Input.TextArea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder='Ask me to change the template…'
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={pending}
            style={{
              borderRadius: 20,
              fontSize: 12,
              padding: '7px 13px',
              resize: 'none',
              flex: 1
            }}
          />
          <SendBtn
            onClick={handleSend}
            disabled={pending || !input.trim()}
          >
            ➤
          </SendBtn>
        </InputRow>
      </Panel>
    </>
  );
};

export default AIChatPanel;
